/**
 * Headless, debounced remote-search combobox for the cost tool's plan and drug
 * typeaheads. It follows the shared `useAddressAutocomplete` accessibility state
 * machine (WAI-ARIA combobox: a `role=combobox` input and a `role=listbox`
 * dropdown, ArrowUp/Down, Enter and Escape, and blur closes), but it searches
 * the backend per keystroke. The shared `useSearchCombobox` filters a
 * prefetched list instead, which doesn't fit the server-side plan and drug
 * catalogue searches.
 *
 * The hook owns the input text, so a selection can clear it and the drug
 * picker accepts the next drug at once. Lookups are debounced (`DEBOUNCE_MS`)
 * and sequence-guarded, so a stale response or error is dropped, never
 * rendered. A failed lookup shows an inline error row in the listbox rather
 * than a dead end; typing again retries.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

/** ARIA combobox attributes to spread onto the text input. */
export interface RemoteComboboxInputProps {
  role: "combobox";
  "aria-expanded": boolean;
  "aria-autocomplete": "list";
  "aria-controls": string;
  "aria-activedescendant": string | undefined;
}

export interface UseRemoteComboboxOptions<T> {
  /** Fetch matches for a query. Read through a ref, so an inline closure is fine. */
  search: (query: string) => Promise<T[]>;
  onSelect: (item: T) => void;
  /** When disabled, the hook never looks up or opens. */
  disabled?: boolean | undefined;
}

export interface UseRemoteComboboxReturn<T> {
  /** Controlled input text. */
  value: string;
  open: boolean;
  loading: boolean;
  /** True when the last lookup failed; the listbox shows the retry hint. */
  error: boolean;
  items: T[];
  /** Keyboard-highlighted option index; -1 = none. */
  activeIndex: number;
  listboxId: string;
  optionId: (index: number) => string;
  comboboxProps: RemoteComboboxInputProps;
  handleInputChange: (value: string) => void;
  handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** Wire to the input's onBlur. It closes the dropdown, which also covers outside clicks. */
  handleBlur: () => void;
  /** Select the i-th item (option click or Enter). Clears the input and closes. */
  select: (index: number) => void;
}

export function useRemoteCombobox<T>({
  search,
  onSelect,
  disabled,
}: UseRemoteComboboxOptions<T>): UseRemoteComboboxReturn<T> {
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [items, setItems] = useState<T[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Stale-response guard: each dispatched lookup captures the sequence number;
  // a response (or error) whose number no longer matches is dropped.
  const seqRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  // Latest search/onSelect without effect churn on inline closures.
  const searchRef = useRef(search);
  searchRef.current = search;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number): string => `${baseId}-option-${index}`;

  // Unmount: cancel the pending debounce and invalidate any in-flight lookup.
  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      seqRef.current += 1;
    },
    [],
  );

  function close(): void {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    seqRef.current += 1;
    setOpen(false);
    setLoading(false);
    setError(false);
    setItems([]);
    setActiveIndex(-1);
  }

  async function runLookup(query: string): Promise<void> {
    const seq = ++seqRef.current;
    try {
      const results = await searchRef.current(query);
      if (seq !== seqRef.current) return; // Stale: a newer lookup or a close won.
      setItems(results);
      setLoading(false);
      setError(false);
      setActiveIndex(-1);
    } catch {
      if (seq !== seqRef.current) return;
      // Never a dead end: keep the dropdown open with the retry hint.
      setItems([]);
      setLoading(false);
      setError(true);
      setActiveIndex(-1);
    }
  }

  function handleInputChange(next: string): void {
    setValue(next);
    if (disabled) return;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    // A changed query invalidates old results before the next debounce fires.
    seqRef.current += 1;
    setItems([]);
    const query = next.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      close();
      return;
    }
    setOpen(true);
    setLoading(true);
    setError(false);
    setActiveIndex(-1);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void runLookup(query);
    }, DEBOUNCE_MS);
  }

  function select(index: number): void {
    const item = items[index];
    if (!item) return;
    onSelectRef.current(item);
    setValue("");
    close();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (!open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation(); // don't also dismiss an enclosing modal
      close();
      return;
    }
    if (items.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (event.key === "Enter") {
      event.preventDefault(); // selecting must not submit a surrounding form
      // With nothing highlighted, Enter takes the first result. The common case
      // is "type enough, hit Enter", and requiring an ArrowDown first wastes a keystroke.
      select(activeIndex >= 0 ? activeIndex : 0);
    }
  }

  return {
    value,
    open,
    loading,
    error,
    items,
    activeIndex,
    listboxId,
    optionId,
    comboboxProps: {
      role: "combobox",
      "aria-expanded": open,
      "aria-autocomplete": "list",
      "aria-controls": listboxId,
      "aria-activedescendant": activeIndex >= 0 ? optionId(activeIndex) : undefined,
    },
    handleInputChange,
    handleKeyDown,
    handleBlur: () => {
      if (open) close();
    },
    select,
  };
}
